/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.
*/

import { TranslatedString, canonicalizeBaseUrl } from "@gnu-taler/taler-util";
import { Footer, useTranslationContext } from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { useState } from "preact/hooks";
import {
  COREBANK_API_BASE_URL_OVERRIDE_KEY,
  DeveloperOverrides,
} from "../developer-settings.js";

export interface DeveloperSettingsProps {
  configuredBackendBaseUrl?: string;
  configuredEnableDemoHeader: boolean;
  configuredShowPublicAccounts: boolean;
  overrides: DeveloperOverrides;
  onApply(overrides: DeveloperOverrides): void;
  onClear(): void;
}

function normalizeHttpBaseUrl(value: string): string {
  const canonical = canonicalizeBaseUrl(value);
  const protocol = new URL(canonical).protocol;
  if (protocol !== "http:" && protocol !== "https:") {
    throw new Error("unsupported protocol");
  }
  return canonical;
}

function BooleanOverrideField({
  name,
  label,
  description,
  configured,
  value,
  onChange,
}: {
  name: string;
  label: TranslatedString;
  description: TranslatedString;
  configured: boolean;
  value: boolean | undefined;
  onChange(value: boolean | undefined): void;
}): VNode {
  const { i18n } = useTranslationContext();
  const configuredLabel = configured
    ? i18n.str`Use configured value (enabled)`
    : i18n.str`Use configured value (disabled)`;
  const options: Array<{
    id: string;
    label: TranslatedString;
    value: boolean | undefined;
  }> = [
    { id: "configured", label: configuredLabel, value: undefined },
    { id: "enabled", label: i18n.str`Force enabled`, value: true },
    { id: "disabled", label: i18n.str`Force disabled`, value: false },
  ];

  return (
    <fieldset>
      <legend class="text-sm font-semibold leading-6 text-onBackground">
        {label}
      </legend>
      <p class="text-sm text-gray-500">{description}</p>
      <div class="mt-3 space-y-2">
        {options.map((option) => (
          <label
            key={option.id}
            class="flex cursor-pointer items-center gap-3 text-sm text-onBackground"
          >
            <input
              type="radio"
              name={name}
              value={option.id}
              checked={value === option.value}
              class="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
              onChange={() => onChange(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function DeveloperSettings({
  configuredBackendBaseUrl,
  configuredEnableDemoHeader,
  configuredShowPublicAccounts,
  overrides,
  onApply,
  onClear,
}: DeveloperSettingsProps): VNode {
  const { i18n } = useTranslationContext();
  const [backendBaseUrl, setBackendBaseUrl] = useState(
    overrides.corebankApiBaseUrl ?? "",
  );
  const [overrideEnableDemoHeader, setOverrideEnableDemoHeader] = useState<
    boolean | undefined
  >(overrides.overrideEnableDemoHeader);
  const [overrideShowPublicAccounts, setOverrideShowPublicAccounts] = useState<
    boolean | undefined
  >(overrides.overrideShowPublicAccounts);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  function apply(): void {
    let normalizedUrl: string | undefined;
    const requestedUrl = backendBaseUrl.trim();
    if (requestedUrl) {
      try {
        normalizedUrl = normalizeHttpBaseUrl(requestedUrl);
      } catch {
        setSaved(false);
        setError(i18n.str`Enter a valid HTTP or HTTPS URL.`);
        return;
      }
    }
    setBackendBaseUrl(normalizedUrl ?? "");
    setError(undefined);
    setSaved(true);
    onApply({
      corebankApiBaseUrl: normalizedUrl,
      overrideEnableDemoHeader,
      overrideShowPublicAccounts,
    });
  }

  function clear(): void {
    setBackendBaseUrl("");
    setOverrideEnableDemoHeader(undefined);
    setOverrideShowPublicAccounts(undefined);
    setError(undefined);
    setSaved(false);
    onClear();
  }

  return (
    <div class="min-h-screen bg-background text-onBackground flex flex-col">
      <header class="bg-brand py-8">
        <div class="mx-auto w-full max-w-3xl px-4 sm:px-6">
          <h1 class="text-3xl font-bold tracking-tight text-onBrand">
            <i18n.Translate>Development settings</i18n.Translate>
          </h1>
        </div>
      </header>
      <main class="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6">
        <form
          class="space-y-8 rounded-lg bg-white p-6 shadow"
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <p class="text-sm text-gray-600">
            <i18n.Translate>
              These overrides are stored only in this browser.
            </i18n.Translate>
          </p>

          <div>
            <label
              class="block text-sm font-semibold leading-6 text-onBackground"
              htmlFor="corebank-api-base-url"
            >
              <i18n.Translate>Corebank API base URL</i18n.Translate>
            </label>
            <input
              id="corebank-api-base-url"
              name="corebank-api-base-url"
              type="url"
              value={backendBaseUrl}
              placeholder={configuredBackendBaseUrl}
              onInput={(event) => {
                setBackendBaseUrl(event.currentTarget.value);
                setSaved(false);
              }}
              aria-invalid={error !== undefined}
              aria-describedby="corebank-api-base-url-help"
              class="mt-2 block w-full rounded-md border-0 px-3 py-2 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm"
            />
            <p
              id="corebank-api-base-url-help"
              class="mt-2 text-sm text-gray-500"
            >
              {configuredBackendBaseUrl ? (
                <i18n.Translate>
                  Leave empty to use the configured URL:{" "}
                  {configuredBackendBaseUrl}
                </i18n.Translate>
              ) : (
                <i18n.Translate>
                  Leave empty to derive the URL from this page.
                </i18n.Translate>
              )}
            </p>
            {error && <p class="mt-2 text-sm text-red-600">{error}</p>}
          </div>

          <BooleanOverrideField
            name="enable-demo-header"
            label={i18n.str`Demo header`}
            description={i18n.str`Control whether the demo header is shown in this browser.`}
            configured={configuredEnableDemoHeader}
            value={overrideEnableDemoHeader}
            onChange={(value) => {
              setOverrideEnableDemoHeader(value);
              setSaved(false);
            }}
          />

          <BooleanOverrideField
            name="show-public-accounts"
            label={i18n.str`Public accounts`}
            description={i18n.str`Control whether public account links are shown in this browser.`}
            configured={configuredShowPublicAccounts}
            value={overrideShowPublicAccounts}
            onChange={(value) => {
              setOverrideShowPublicAccounts(value);
              setSaved(false);
            }}
          />

          {saved && (
            <p role="status" class="text-sm font-medium text-green-700">
              <i18n.Translate>Overrides saved.</i18n.Translate>
            </p>
          )}

          <div class="flex flex-wrap gap-3">
            <button
              type="submit"
              class="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <i18n.Translate>Apply overrides</i18n.Translate>
            </button>
            <button
              type="button"
              class="rounded-md bg-white px-4 py-2 text-sm font-semibold text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={clear}
            >
              <i18n.Translate>Clear overrides</i18n.Translate>
            </button>
            <a
              href="#/"
              class="rounded-md px-4 py-2 text-sm font-semibold text-primaryDark hover:bg-primaryContainer/40"
            >
              <i18n.Translate>Back to bank</i18n.Translate>
            </a>
          </div>
        </form>
      </main>
      <Footer
        testingUrlKey={COREBANK_API_BASE_URL_OVERRIDE_KEY}
        variant="demo"
      />
    </div>
  );
}
